/**
 * useChatbotSocket — one hook for both admin WS channels.
 *
 * Modes:
 *   - 'feed'         → ProjectFeed DO, receives events for ALL conversations in
 *                       the project (new message, typing, status change).
 *   - 'conversation' → ConversationRoom DO, full-duplex for a single chat:
 *                       stream tokens, typing, read receipts, send messages.
 *
 * Auth: admin JWT is passed via `?token=` query param because browsers cannot
 * set headers on the native WebSocket constructor. The backend's authMiddleware
 * already accepts token via query for WS routes. The token itself comes from
 * `getAuthToken()` — the same resolver the axios interceptor uses — so ghost
 * (impersonation) tabs connect as the impersonated user and the two transports
 * can never drift apart.
 *
 * Reconnect: exponential backoff (1s → 30s max). No polling, no intervals
 * beyond backoff. On reconnect we emit a 'reconnected' synthetic event so the
 * consumer can decide whether to re-hydrate via REST.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { getAuthToken } from '@/stores/auth';

export type ChatEvent = {
  v: 1;
  type: string;
  conversationId?: string;
  projectId: string;
  at: number;
  data?: unknown;
};

type Mode =
  | { kind: 'feed'; projectId: string }
  | { kind: 'conversation'; projectId: string; conversationId: string };

type Status = 'connecting' | 'open' | 'closed' | 'error';

function wsBaseUrl(): string {
  const http = import.meta.env.VITE_API_URL || '/api/v1';
  // Resolve relative paths against current origin.
  const abs = http.startsWith('http') ? http : `${window.location.origin}${http}`;
  return abs.replace(/^http/, 'ws');
}

export function useChatbotSocket(mode: Mode | null, onEvent: (ev: ChatEvent) => void) {
  const [status, setStatus] = useState<Status>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1000);
  const deadRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const send = useCallback((payload: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!mode) return;
    deadRef.current = false;

    const connect = () => {
      if (deadRef.current) return;
      // Read on every connect attempt, not once per mount: a reconnect after a
      // token refresh must pick up the new token.
      const token = getAuthToken() || '';
      const base = wsBaseUrl();
      let url: string;
      if (mode.kind === 'feed') {
        url = `${base}/chatbot-ws/admin/feed?projectId=${encodeURIComponent(
          mode.projectId
        )}&token=${encodeURIComponent(token)}`;
      } else {
        url = `${base}/chatbot-ws/admin/conversation/${encodeURIComponent(
          mode.conversationId
        )}?projectId=${encodeURIComponent(mode.projectId)}&token=${encodeURIComponent(token)}`;
      }

      setStatus('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 1000;
        setStatus('open');
      };
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data) as ChatEvent;
          onEventRef.current(ev);
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onerror = () => setStatus('error');
      ws.onclose = () => {
        setStatus('closed');
        if (deadRef.current) return;
        const delay = Math.min(backoffRef.current, 30_000);
        backoffRef.current = Math.min(backoffRef.current * 2, 30_000);
        setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      deadRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode?.kind, (mode as any)?.projectId, (mode as any)?.conversationId]);

  return { status, send };
}
